// src/constants/constants.ts
import esheHero from "../assets/images/eshe_hero.png";
import esheServices from "../assets/images/eshe_services.png";
import esheDoctorApp from "../assets/images/eshe_doctor_app.png";
import esheUserApp from "../assets/images/eshe_user_app.png";
import esheLabs from "../assets/images/eshe_labs.png";

export interface Project {
  id: number;
  title: string;
  description: string[];
  images: any[];
  tags: string[];
  skills: string[];
  source: string;
  visit?: string;
  heroku?: boolean;
}

export const projects: Project[] = [
  {
    title: "Eshe E-health",
    description: [
      "Architected and built a comprehensive telemedicine platform (<strong>Spring Boot</strong>, <strong>React</strong>) supporting video consultations, ambulance dispatching, pharmacy services, and lab management.",
      "Implemented a real-time video call feature (<strong>Spring Reactive Web</strong>) paired with AI-powered note-taking via the <strong>OpenAI API</strong>, achieving near-perfect voice-to-text accuracy.",
      "Engineered a <strong>RAG</strong> (Retrieval-Augmented Generation) pipeline over patient data to streamline clinical queries and significantly reduce diagnosis times.",
      "Utilized <strong>Grafana</strong> for system observability to identify backend bottlenecks; optimized complex database queries and cached common drug records to increase patient onboarding by 200.",
      "Integrated <strong>Stripe</strong> and <strong>M-Pesa</strong> payment gateways, expanding market reach across local and international card users.",
      "Engineered a multi-platform ecosystem with web portals, doctor consultation scheduling, and mobile user applications.",
    ],
    images: [esheHero,esheServices,esheLabs,esheDoctorApp, esheUserApp],
    tags: [
      "React.js",
      "Spring Boot",
      "Spring Reactive Web",
      "OpenAI API",
      "RAG / AI",
      "MySQL",
      "Grafana",
      "M-Pesa",
      "Stripe",
    ],
    skills: [
      "Telemedicine Platform Architecture",
      "Real-time Video Consultations",
      "AI-Powered Medical Note-Taking",
      "RAG Clinical Data Query Pipeline",
      "M-Pesa & Stripe Payment Gateways",
      "System Observability & Monitoring (Grafana)",
      "Database Query Optimization & Caching",
      "Healthcare Regulatory & Privacy Compliance",
    ],
    source: "",
    visit: "https://esheehealth.co.ke",
    id: 1,
  },
];

interface TimeLine {
  date: string;
  title: string;
  subtitle: string;
  description: string[];
}

// Experience data matching resume
export const timeline: TimeLine[] = [
  {
    date: "08/2025 – Present",
    title: "Software Engineer (Part-time Freelance)",
    subtitle: "Eshe E-health · Nairobi, Kenya",
    description: [
      "Architected and built a telemedicine platform (<strong>Spring Boot</strong>, <strong>React</strong>) supporting video consultations, ambulance dispatching, pharmacy services, and lab management.",
      "Implemented a real-time video call feature (<strong>Spring Reactive Web</strong>) paired with AI-powered note-taking via the <strong>OpenAI API</strong>, achieving near-perfect voice-to-text accuracy.",
      "Engineered a <strong>RAG</strong> (Retrieval-Augmented Generation) pipeline over patient data to streamline clinical queries and significantly reduce diagnosis times.",
      "Utilized <strong>Grafana</strong> for system observability to identify backend bottlenecks; optimized complex database queries and cached common drug records to increase patient onboarding by 200.",
      "Integrated <strong>Stripe</strong> and <strong>M-Pesa</strong> payment gateways, expanding market reach across local and international card users.",
    ],
  },
  {
    date: "10/2024 – 03/2026",
    title: "Backend Software Engineer",
    subtitle: "TandaPay · Nairobi, Kenya",
    description: [
      "Developed TandaPay's wallet infrastructure, supporting deposits, withdrawals, internal balance management, and real-time transaction tracking.",
      "Implemented the wallet's USSD short code and the reconciliation module. Wallet total transactions stood at <strong>KSh 2.589 Billion ($20M)</strong> in the first 3 months.",
      "Used Microservices (using <strong>Spring Boot</strong> and <strong>Go</strong>) and <strong>Kafka</strong> extensively to process transactions and ensure financial compliance laid out by CBK and Tanda customers.",
      "Created secure payment services (internal/external channels) with maker-checker controls by leveraging async callbacks & webhooks to ensure auditability.",
      "Created and optimized different <strong>React.js</strong> (Tailwind CSS) dashboards by implementing memoization and route-based lazy loading, reducing the initial load time from 2.1s to 1.4s by code-splitting non-critical components and memoizing expensive rendering.",
      "Implemented Aspect Oriented Programming (AOP) to automate audit logging at different application layers, ensuring financial grade accountability.",
    ],
  },
  {
    date: "01/2024 – 09/2024",
    title: "Full Stack Software Engineer",
    subtitle: "E&M Technologies · Nairobi, Kenya",
    description: [
      "Developed a power management application (<strong>Spring Boot</strong>, <strong>MySQL</strong>) with automatic controls and MongoDB IoT fast data storage (10K+ readings/sec). Implemented unit/integration/e2e tests (<strong>JUnit, Mockito, Rest Assured</strong>), ensuring 99.99% uptime and cutting debug time.",
      "Created different indices for read-heavy tables, reducing latency greatly; application adopted by 1,000 clients.",
      "Optimized a tax analysis platform (<strong>Spring Boot</strong>, <strong>React.js</strong>) by decoupling monolithic backend into <strong>Kafka</strong>-based microservices, cutting latency by 100ms in 2 months—enabling faster tax filings during peak season.",
      "Redesigned frontend with <strong>Material UI</strong> and <strong>Tailwind CSS</strong>, ensuring a responsive, intuitive interface that reduced user errors by 15% and improved compliance reporting efficiency.",
      "Used <strong>Docker Compose</strong> for application deployment for consistent performance and predictability.",
    ],
  },
  {
    date: "01/2023 – 12/2023",
    title: "AI/ML Software Engineer & Contributor (Outreachy)",
    subtitle: "Ersilia Open-Source Initiative · Remote (Barcelona, Spain)",
    description: [
      "Improved ErsiliaHub (<strong>Node.js</strong>, <strong>React.js</strong>, and <strong>MongoDB</strong>) for machine learning model consumption, deploying 100+ machine learning models to organizations in Cameroon and other Global South nations.",
      "Selected as one of 63 successful remote applicants from 1,200 worldwide applicants to work at Ersilia through <strong>Outreachy Internships</strong>.",
      "Implemented <strong>Jest</strong> automated testing for the backend, achieving 99% test coverage. Ensured robust API endpoints, model input validation, and error handling, reducing production bugs massively and accelerating release time to minutes.",
      "Set up real-time system monitoring using <strong>Grafana</strong>, reducing incident resolution time greatly, and created GitHub CI/CD pipelines to automate model deployment, testing, and releases to Docker Hub and AWS S3.",
      "Worked in Python, modifying, training, and testing 30+ open source machine learning models using NLP (atoms treated as language tokens), generative models, and multitask learning.",
      "Created a website for consuming models using <strong>React.js</strong> with Test Driven Development (using <strong>Jest</strong>), increasing users by 7%.",
    ],
  },
];

export const education = [
  {
    date: "09/2017 – 12/2022",
    title: "BSc. Computer Science Major (Second Class Upper Division)",
    subtitle: "Moi University · Uasin Gishu, Kenya",
    description: [
      "<strong>Relevant Coursework:</strong> Software Engineering Principles and Practices, Data Structures and Algorithms, Networking, AI/ML",
      "<strong>Grade:</strong> Second Class Upper Division",
      "<strong>Activities:</strong> Developer Circles from Facebook (Student Mentor), Google Developer Student Clubs (Member)",
    ],
  },
];
